import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFormComponent, ModalComponent } from 'core/components';
import {
  PopUpService,
  StudentsService,
  ClientsService,
  SubscriptionsService,
  UsersService,
  JobsService,
  ConfigurationsService,
  LocalesService
} from 'core/services';
import { StudentsQuestionsService } from '../students-questions.service';
import { Client } from 'core/models/client';
import { Subscription } from 'rxjs';

/*
EXAMPLE CSV:
Nombre,Apellido,Correo electrónico,Teléfono,DNI,Comentarios,Idioma,Alias,tag1,tag2,tag3
Barry,Freeman,barry.freeman89@pharmacon.com,2117029922,11111111A,012345A,,tagy,,,
Daisy,Powell,daisy.powell83@pharmacon.com,2887907284,22222222B,012345B,,tagy,,,
Everett,Gonzalez,everett.gonzales47@pharmacon.com,4961837444,33333333C,012345C,,tagy,,,
Ray,Lawson,ray.lawson96@pharmacon.com,6209126384,44444444D,012345D,,tagy,,,
Clifton,Dunn,clifton.dunn34@pharmacon,7022464885,55555555E,012345E,,tagy,,,
Kristina,Horton,kristina.horton10pharmacon.com,7474644001,66666666F,012345F,,tagy,,,
*/

@Component({
  selector: 'lms-students-import',
  templateUrl: './students-import.component.html',
  styleUrls: ['./students-import.component.scss']
})
export class StudentsImportComponent implements OnInit, OnDestroy {
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;
  @ViewChild(ModalComponent, { static: false }) public modal: ModalComponent;

  public clientId: string;
  public questions: any[];
  public infoMessage: string;

  private client: Client;
  private canAddStudents: boolean;
  private seatsAvailable: number;
  private successCount = 0;
  private errorCount = 0;
  private errors = [];

  private messageConfigSubscription: Subscription = new Subscription();

  constructor(
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private translate: TranslateService,
    private popupService: PopUpService,
    private studentsQuestionsService: StudentsQuestionsService,
    private studentsService: StudentsService,
    private clientsService: ClientsService,
    private usersService: UsersService,
    private jobsService: JobsService,
    private subscriptionsService: SubscriptionsService,
    private configurationsService: ConfigurationsService,
    private localesService: LocalesService
  ) {}

  ngOnInit() {
    this.activatedRoute.data.subscribe((data) => {
      this.client = data['client'];
      this.clientId = this.client ? this.client.id : null;
      if (this.clientId) {
        this.checkSubscription();
      }
      this.questions = this.studentsQuestionsService.getImportStudentsQuestions(this.client);
    });

    const key = 'lms.students-import.info';
    this.messageConfigSubscription = this.configurationsService
      .getInfoMessage$(
        'platformMessages',
        'fields.key',
        key,
        'text',
        this.localesService.getBrowserLocale(),
        false,
        true,
        true
      )
      .subscribe((message) => {
        if (message) {
          this.infoMessage = message;
        }
      });
  }

  public ngOnDestroy() {
    this.messageConfigSubscription.unsubscribe();
  }

  public setClient(data: any) {
    this.clientId = data.clientId;
    if (this.clientId) {
      this.clientsService.getClient(this.clientId).subscribe((client) => {
        this.client = client;
        this.checkSubscription();
      });
    }
  }

  public addImportedStudents(studentsToImport) {
    this.studentsService
      .createUsers(this.clientId, studentsToImport.data)
      .then((response) => {
        this.successCount = response.users.length;
        this.errorCount = response.errors.length;

        response.errors.map((studentNotCreated) => {
          if (studentsToImport.data.find((obj) => obj.email === studentNotCreated.email)) {
            const error = {
              email: studentNotCreated.email,
              message: studentNotCreated.error
            };
            this.errors.push(error);
          }
        });

        if (this.successCount > 0) {
          if (this.errorCount > 0) {
            this.showWarning('students.addListWarning', 'students.addListMessage');
          } else {
            this.showSuccess('students.addListSuccess', 'students.addListMessage');
          }
        } else {
          this.showError('students.addListError', 'students.addListMessage');
        }
      })
      .catch();
  }

  private showSuccess(title, description) {
    this.popupService
      .showSuccess({
        title: this.translate.instant(title),
        html: this.translate.instant(description, {
          successCount: this.successCount,
          errorCount: this.errorCount
        })
      })
      .then(() => {
        this.location.back();
      });
  }

  private showWarning(title, description) {
    this.popupService
      .showWarning({
        title: this.translate.instant(title),
        html: this.translate.instant(description, {
          successCount: this.successCount,
          errorCount: this.errorCount
        }),
        showCancelButton: true,
        reverseButtons: true,
        focusConfirm: false,
        confirmButtonText: this.translate.instant('actions.accept'),
        cancelButtonText: this.translate.instant('actions.showInfo')
      })
      .then(() => {
        // Ok
        this.location.back();
      })
      .catch(() => {
        // More info
        this.showErrorInfo();
      });
  }

  private showError(title, description) {
    this.popupService
      .showError({
        title: this.translate.instant(title),
        html: this.translate.instant(description, {
          successCount: this.successCount,
          errorCount: this.errorCount
        }),
        showCancelButton: true,
        reverseButtons: true,
        focusConfirm: false,
        confirmButtonText: this.translate.instant('actions.accept'),
        cancelButtonText: this.translate.instant('actions.showInfo')
      })
      .then(() => {
        // Ok
        this.location.back();
      })
      .catch(() => {
        // More info
        this.showErrorInfo();
      });
  }

  private showErrorInfo() {
    let existentEmailErrors: string = '';
    let otherErrors: string = '';
    let swalHtml: string = '';

    this.errors.forEach((error) => {
      if (error.message === 'User email already exists: ' + error.email) {
        existentEmailErrors = existentEmailErrors.concat(`<li>${error.email}</li>`);
      } else {
        otherErrors += `<li>${error.email} (${error.message})</li>`;
      }
    });

    if (existentEmailErrors.length) {
      swalHtml = swalHtml.concat(
        '<b>',
        this.translate.instant('students.addListErrorInfoExist'),
        '</b>',
        '<ul>',
        existentEmailErrors,
        '</ul>'
      );
    }
    if (otherErrors.length) {
      swalHtml = swalHtml.concat(
        '<b>',
        this.translate.instant('students.addListErrorInfoOther'),
        '</b>',
        '<ul>',
        otherErrors,
        '</ul>'
      );
    }

    this.popupService
      .showConfirm({
        type: 'info',
        html: swalHtml,
        showCloseButton: true
      })
      .then(() => {
        // Ok
        this.location.back();
      })
      .catch(() => {
        // Close button
        this.location.back();
      });
  }

  private checkSubscription() {
    if (this.client.subscriptions.users) {
      this.subscriptionsService.getClientSubscription(this.client.id).then((subscription) => {
        if (!subscription) {
          this.canAddStudents = false;
        } else {
          this.seatsAvailable = subscription.rateLimit - subscription.rateConsumed;
          this.canAddStudents = !!this.seatsAvailable && new Date(subscription.endDate).getTime() - Date.now() > 0;
        }
        if (!this.canAddStudents) {
          const description =
            this.seatsAvailable === 0
              ? 'students.subscriptionCheck.noSeats'
              : 'students.subscriptionCheck.noActiveSubscription';
          this.popupService
            .showError({
              title: this.translate.instant('students.subscriptionCheck.cantAddStudents'),
              text: this.translate.instant(description)
            })
            .then(() => {
              this.location.back();
            });
        }
      });
    } else {
      // Credits client
      this.canAddStudents = true;
    }
  }

  public goBack() {
    this.location.back();
  }

  onSaveCsvData(csvData: any) {
    this.usersService
      .importCsvUsers(this.clientId, csvData)
      .then((res) => this.jobsService.manageJob(res.jobId, this.jobsService.jobTypes.CREATE_USERS_BATCH));
  }
}
